import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { markRead } from "../redux/slices/notificationsSlice";

// NOTIFY-F006 review fix (L4, AC-F7.7) — deterministic mark-read for the push
// `notificationclick` path. public/service-worker.js appends `?notification=<id>` to the
// deep link it navigates an existing tab to (or opens a new one with) rather than relying
// solely on postMessage: a `postMessage` to a client that isn't mounted/focused yet (a cold
// tab, or a brand-new window the SW just opened with `clients.openWindow`) can race the
// app's own bootstrapping and simply be missed, whereas a query param survives because it
// is part of the URL the client actually loads. NotificationBell.js's `message` listener
// stays in place as a secondary signal for the already-focused-tab case, where SW -> client
// messaging is reliable.
//
// Rendered once, high in the tree (Header.js) so it fires no matter which route the deep
// link lands on, and reads the param on mount and on every subsequent route change (a
// push's deep link can land while the app is already open on some other route).
const NotificationClickHandler = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get("notification");
    if (idParam == null) {
      return;
    }
    const id = Number(idParam);
    if (!Number.isNaN(id)) {
      dispatch(markRead(id));
    }
    // Strip the param so it can't be re-processed on a later unrelated navigation (e.g.
    // browser back/forward) and doesn't linger in the visible URL.
    params.delete("notification");
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : "" },
      { replace: true }
    );
    // Deliberately keyed on the raw search string only — `dispatch`/`navigate` are stable
    // and `location.pathname` doesn't need to retrigger this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return null;
};

export default NotificationClickHandler;
